import {
  AlertCard,
  AlertCardTitle,
  Button,
  Checkbox,
  Combobox,
  FormGroup,
  FormLabel,
  Input,
  noop,
  Stack,
} from '@strongdm/design-system'
import clsx from 'clsx'
import Cookie from 'js-cookie'
import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as Yup from 'yup'

import { LandingLayout } from '../LandingLayout/LandingLayout'
import { forgetLogin, getRememberedLogin, rememberLogin } from './remember'
import { ProbeResult, useProbe } from './useProbe'

export const LoginPage = () => {
  return (
    <LandingLayout>
      <h1 className={clsx('sdm--title3', 'sdm--margin-top--s0')}>
        Log in to StrongDM
      </h1>
      <LoginForm />
    </LandingLayout>
  )
}

type ControlPlaneOption = {
  id: string
  label: string
  value: string
  probeResult: ProbeResult
}

const schema = Yup.string().required().email()

const useLogoutReason = () => {
  return useMemo(() => {
    const reason = new URLSearchParams(window.location.search).get('reason')
    if (reason === null) {
      return undefined
    }
    if (reason === 'timeout') {
      return 'You have been logged out due to inactivity.'
    }
    return 'You have been logged out.'
  }, [])
}

const useFrom = () => {
  return useMemo(() => {
    return new URLSearchParams(window.location.search).get('from')
  }, [])
}

const LoginForm = () => {
  const rememberedLogin = useMemo(() => getRememberedLogin(), [])
  const [rememberMe, setRememberMe] = useState(rememberedLogin !== undefined)
  useEffect(() => {
    if (rememberedLogin && !rememberMe) {
      forgetLogin()
    }
  }, [rememberMe, rememberedLogin])

  const [email, setEmail] = useState(rememberedLogin?.email ?? '')
  const logoutReason = useLogoutReason()
  const [error, setError] = useState(logoutReason)
  const [submitting, setSubmitting] = useState<boolean>()
  useEffect(() => {
    const pageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setSubmitting(false)
      }
    }
    window.addEventListener('pageshow', pageShow)
    return () => {
      window.removeEventListener('pageshow', pageShow)
    }
  })
  const from = useFrom()
  const [selectedControlPlane, setSelectedControlPlane] = useState(
    from || rememberedLogin?.controlPlane,
  )
  const [controlPlaneOptions, setControlPlaneOptions] = useState<
    ControlPlaneOption[]
  >([])

  const emailValid = useMemo(() => schema.isValidSync(email), [email])

  const probe = useProbe()

  const onSubmit = useCallback(
    async (arg: FormEvent<any> | 'autoSubmitted') => {
      if (arg !== 'autoSubmitted') {
        arg.preventDefault()
        // Hide error once the user hits "next", even if the form gets set back
        // to the initial step.
        setError(undefined)
      }
      setSubmitting(true)

      // If the user has selected a control plane, redirect to it.
      if (controlPlaneOptions.length > 0) {
        const selectedOption = controlPlaneOptions.find(
          (x) => x.value === selectedControlPlane,
        )
        if (selectedOption) {
          complete(email, selectedOption, rememberMe)
          return
        }
      }

      try {
        const options = (await probe(email)).map((probeResult) => {
          return {
            id: probeResult.name,
            label: probeResult.name,
            value: probeResult.appDomain,
            probeResult,
          }
        })
        // If the user does not exist in any control plane, redirect to trial
        // signup form.
        if (options.length === 0 && arg !== 'autoSubmitted') {
          window.location.assign(
            `https://strongdm.com/signup?email=${encodeURIComponent(email)}`,
          )
          return
        }

        // If the user only exists in one control plane, redirect there
        // immediately. But if this form submission came from an auto-submission
        // on mount, don't redirect. Give the user a chance to enter a different
        // email.
        if (options.length === 1 && arg !== 'autoSubmitted') {
          complete(email, options[0], rememberMe)
          return
        }

        // If the user exists in multiple control planes, let them select which
        // one to login to.
        setSubmitting(false)
        setControlPlaneOptions(options)
        if (
          options.length > 0 &&
          !options.find((x) => x.value == selectedControlPlane)
        ) {
          setSelectedControlPlane(options[0].value)
        }
      } catch (err) {
        setSubmitting(false)
        setError(
          'Something went wrong and the issue has been reported. In the meantime, try clicking back in your browser or refreshing the page.',
        )
        // eslint-disable-next-line no-console
        console.error(err)
      }
    },
    [probe, controlPlaneOptions, email, selectedControlPlane, rememberMe],
  )

  // If the user selected "remember me", then we want to auto-submit the form
  // when the component first mounts, so we can show the region selector as soon
  // as possible. However, if there ends up being only one control plane option,
  // we DO NOT want to redirect the user directly there; we want to give them a
  // chance to enter a different email. Passing autoSubmitted = true into the
  // onSubmit handler makes this happen.
  const autoSubmittedRef = useRef(false)
  useEffect(() => {
    if (rememberedLogin && !autoSubmittedRef.current) {
      autoSubmittedRef.current = true
      onSubmit('autoSubmitted').catch(noop)
    }
  }, [onSubmit, rememberedLogin])

  return (
    <form noValidate onSubmit={onSubmit}>
      <Stack type="column" spacing="s4">
        {error && (
          <AlertCard volume="quiet" context="negative">
            <AlertCardTitle>{error}</AlertCardTitle>
          </AlertCard>
        )}
        <FormGroup>
          <FormLabel>Email address</FormLabel>
          <Input
            placeholder="Your email address"
            value={email}
            onChange={(event) => {
              setEmail(event.currentTarget.value)
              setControlPlaneOptions([])
            }}
            disabled={submitting}
            autoFocus
          />
        </FormGroup>
        {controlPlaneOptions.length > 1 && (
          <FormGroup>
            <FormLabel>Region</FormLabel>
            <Combobox
              id="controlPlanes"
              name="controlPlanes"
              options={controlPlaneOptions}
              value={selectedControlPlane}
              placeholder="Select a region"
              disabled={submitting}
              onSelect={(value) => {
                if (typeof value === 'string') setSelectedControlPlane(value)
              }}
              readOnly
            />
          </FormGroup>
        )}
        <Button
          volume="loud"
          type="submit"
          disabled={submitting || !emailValid}
        >
          {submitting ? 'Loading...' : 'Next'}
        </Button>
        <Stack hAlign="space-between">
          <Checkbox
            name="rememberMe"
            className="sdm--small"
            checked={rememberMe}
            onChange={(event) => setRememberMe(event.currentTarget.checked)}
          >
            Remember my email
          </Checkbox>
        </Stack>
      </Stack>
    </form>
  )
}

const complete = (
  email: string,
  selectedControlPlane: ControlPlaneOption,
  rememberMe: boolean,
) => {
  if (rememberMe) {
    rememberLogin({ email, controlPlane: selectedControlPlane.value })
  }
  setAutoSubmitCookie(email, rememberMe)
  window.location.assign(`${selectedControlPlane.probeResult.url}/app/login`)
}

const setAutoSubmitCookie = (email: string, rememberMe: boolean) => {
  // set autosubmit cookie for the control plane's login form to pick up
  Cookie.set(
    'sdm_login_autosubmit',
    JSON.stringify({
      id: email,
      rememberMe: rememberMe,
    }),
    {
      domain:
        window.location.hostname === 'localhost'
          ? 'localhost'
          : '.strongdm.com',
      path: '/',
      secure: window.location.hostname !== 'localhost',
      expires: new Date(Date.now() + 60 * 1000), // 1 minute from now
      sameSite: 'strict',
    },
  )
}
